// Removes the GPS blocks from a JPEG's EXIF before upload (owner 2026-11-01), with no re-encode.
// Each GPS IFD (every GPSInfo pointer of IFD0, and of IFD1, the thumbnail IFD) is emptied in place:
// its entry count becomes 0 and its entries, their out-of-line values and its next-IFD link are
// zeroed. The GPSInfo pointers then point at empty IFDs (valid TIFF). Nothing moves: every other tag
// (orientation included), every other segment and the compressed image data stay byte-for-byte the
// same, and the file keeps its size. Both byte orders (II / MM).
// No library: piexifjs-like tools rebuild the whole EXIF block (moving offsets, dropping maker notes).
import logger from "./logger";

const GPS_INFO_TAG = 0x8825;
/** Bytes per value of each TIFF field type (1..12). */
const TYPE_SIZE = [0, 1, 1, 2, 4, 8, 1, 1, 2, 4, 8, 4, 8];
/** EXIF lives in the first segments (APP1, at most 64 KB); only this much of the file is read. */
const HEAD_BYTES = 1 << 20;

class MalformedExif extends Error {}

function fail(why: string): never {
  throw new MalformedExif(why);
}

/**
 * Empties the GPS IFD of a JPEG in place. Returns true when it changed `bytes`, false when there is
 * nothing to remove (not a JPEG, no EXIF, no GPS). Throws on malformed EXIF (the caller then uploads
 * the original). `bytes` may be just the head of the file: running off its end throws.
 */
export function stripGpsInPlace(bytes: Uint8Array): boolean {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return false;
  let pos = 2;
  for (;;) {
    if (pos + 4 > bytes.length) fail("ran off the head before the image data");
    if (bytes[pos] !== 0xff) fail("not a marker");
    const marker = bytes[pos + 1];
    if (marker === 0xff) {
      pos++; // fill byte
      continue;
    }
    if (marker === 0xda || marker === 0xd9) return false; // image data / end: no EXIF before it
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      pos += 2; // no length
      continue;
    }
    const len = (bytes[pos + 2] << 8) | bytes[pos + 3];
    if (len < 2) fail("bad segment length");
    const start = pos + 4;
    const end = pos + 2 + len;
    if (end > bytes.length) fail("segment past the head");
    if (
      marker === 0xe1 &&
      len >= 16 &&
      bytes[start] === 0x45 && // "Exif\0\0"
      bytes[start + 1] === 0x78 &&
      bytes[start + 2] === 0x69 &&
      bytes[start + 3] === 0x66 &&
      bytes[start + 4] === 0 &&
      bytes[start + 5] === 0
    ) {
      return emptyGpsIfd(bytes, start + 6, end);
    }
    pos = end;
  }
}

/** The TIFF block is bytes[tiff, end). */
function emptyGpsIfd(bytes: Uint8Array, tiff: number, end: number): boolean {
  const size = end - tiff;
  const view = new DataView(bytes.buffer, bytes.byteOffset + tiff, size);
  const order = view.getUint16(0);
  if (order !== 0x4949 && order !== 0x4d4d) fail("bad byte order");
  const le = order === 0x4949;
  const u16 = (o: number) => {
    if (o < 0 || o + 2 > size) fail("offset out of range");
    return view.getUint16(o, le);
  };
  const u32 = (o: number) => {
    if (o < 0 || o + 4 > size) fail("offset out of range");
    return view.getUint32(o, le);
  };
  if (u16(2) !== 42) fail("not TIFF");

  // Every GPSInfo pointer of IFD0 (a writer may repeat the tag) and of IFD1, the thumbnail IFD
  // (read once, bounds-checked; its own next link is not followed).
  const gpsPointers = (ifd: number): number[] => {
    const out: number[] = [];
    const count = u16(ifd);
    if (ifd + 2 + count * 12 + 4 > size) fail("IFD out of range");
    for (let i = 0; i < count; i++) {
      const entry = ifd + 2 + i * 12;
      if (u16(entry) === GPS_INFO_TAG) {
        const at = u32(entry + 8);
        if (at) out.push(at);
      }
    }
    return out;
  };
  const ifd0 = u32(4);
  const pointers = gpsPointers(ifd0);
  const ifd1 = u32(ifd0 + 2 + u16(ifd0) * 12);
  if (ifd1) pointers.push(...gpsPointers(ifd1));

  // Check every GPS IFD and its out-of-line values before anything is written.
  const ranges: [number, number][] = [];
  for (const gps of new Set(pointers)) {
    const count = u16(gps);
    const entriesEnd = gps + 2 + count * 12 + 4; // entries + next-IFD link
    if (entriesEnd > size) fail("GPS IFD out of range");
    if (count === 0) continue;
    for (let i = 0; i < count; i++) {
      const entry = gps + 2 + i * 12;
      const type = u16(entry + 2);
      if (!TYPE_SIZE[type]) fail("unknown field type");
      const bytesLen = TYPE_SIZE[type] * u32(entry + 4);
      if (bytesLen > 4) {
        const at = u32(entry + 8);
        if (at + bytesLen > size) fail("GPS value out of range");
        ranges.push([at, bytesLen]);
      }
    }
    ranges.push([gps, entriesEnd - gps]); // count = 0, entries and link zeroed
  }
  if (ranges.length === 0) return false;
  for (const [at, len] of ranges) bytes.fill(0, tiff + at, tiff + at + len);
  return true;
}

/**
 * The JPEG to upload: `file` itself when it has no GPS (or is not a JPEG / is malformed), else a
 * same-size File whose GPS IFD is empty. Only the head is copied; the rest is the original's slice.
 */
export async function withoutGps(file: File): Promise<File> {
  try {
    const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
    if (!stripGpsInPlace(head)) return file;
    return new File([head, file.slice(head.length)], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch (err) {
    logger.warn("[jpegGps] EXIF not readable, uploading the original", err);
    return file;
  }
}
